import { useCallback, useEffect, useState } from 'react';
import { normaliseRateCard, type RateCard } from '@mypoz/shared';

import { fetchRateRow } from './api.js';

export interface RateCardState {
  readonly status: 'loading' | 'ready' | 'error';
  /** Undefined when ready but the vendor has no usable table — "rates unavailable". */
  readonly card?: RateCard;
  readonly retry: () => void;
}

/**
 * The China Payment rate card (MYP-59), loaded when a China Payment page opens. Not app-wide and
 * not kept between visits: the rates change daily, and the old website reads them fresh too.
 */
export function useRateCard(): RateCardState {
  const [status, setStatus] = useState<RateCardState['status']>('loading');
  const [card, setCard] = useState<RateCard | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Only the newest attempt's answer lands (StrictMode's double mount, or a retry mid-flight).
    let current = true;
    setStatus('loading');
    fetchRateRow().then(
      (row) => {
        if (!current) return;
        setCard(normaliseRateCard(row));
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[china-payment] could not load the rates', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return card ? { status, card, retry } : { status, retry };
}
